import { getToken } from "../utils/getToken";
import { campaignApi } from "../api/campaignApi";
import {
  Campaign,
  CampaignDetails,
  CampaignRegisteredChannel,
  CompletedCampaignViewModel,
  EstimatedTotalMembersRequest,
  CampaignInfo,
} from "../models/campaign";
import { ChannelEnum, StatusEnum } from "../constants/enums";
import { DateRange } from "../models/time";
import { Voucher } from "../models/voucher";
import { EVoucher } from "../models/evoucher";
import { Option } from "../models/option";

import { AccountBrandOutletSetting } from "../models/accountBrandOutletSetting";
import { MessageTemplateData } from "../models/messageTemplates";

export const campaignService = {
  getAllCampaign: async (): Promise<Option[] | null> => {
    try {
      const token = getToken();
      if (!token) return null;
      const response = await campaignApi.getAllCampaign(token);
      return response.data.result;
    } catch (error) {
      console.error("Failed to fetch campaign:", error);
      return null;
    }
  },
  getAllCampaignDetails: async (
    brandId: string,
  ): Promise<Campaign[] | null> => {
    try {
      const token = getToken();
      if (!token) return null;
      if (brandId) {
        const response = await campaignApi.getAllCampaignDetails(
          token,
          brandId,
        );
        return response.data.result.CampaignDetails;
      }
      return null;
    } catch (error) {
      console.error("Failed to fetch campaign details:", error);
      return null;
    }
  },
  getRegisteredChannel: async (
    brandId: string,
  ): Promise<CampaignRegisteredChannel[] | null> => {
    try {
      const token = getToken();
      if (!token) return null;
      if (brandId) {
        const response = await campaignApi.getRegisteredChannel(token, brandId);
        return response.data.result.CampaignRegisteredChannel;
      }
      return null;
    } catch (error) {
      console.error("Failed to fetch registered channel:", error);
      return null;
    }
  },
  createCampaignRegisteredChannel: async (
    CampaignRegisteredChannel: CampaignRegisteredChannel,
  ): Promise<CampaignRegisteredChannel | null> => {
    try {
      const token = getToken();
      if (!token) return null;
      if (CampaignRegisteredChannel) {
        const response = await campaignApi.createCampaignRegisteredChannel(
          token,
          CampaignRegisteredChannel,
        );
        return response.data.result;
      }
      return null;
    } catch (error) {
      console.error("Failed to create Campaign Registered Channel:", error);
      return null;
    }
  },
  reactiveCampaignRegisteredChannelStatus: async (
    Id: number,
  ): Promise<CampaignRegisteredChannel | null> => {
    try {
      const token = getToken();
      if (!token) return null;
      if (Id) {
        const response =
          await campaignApi.reactiveCampaignRegisteredChannelStatus(token, Id);
        return response.data.result;
      }
      return null;
    } catch (error) {
      console.error("Failed to reactive Campaign Registered Channel: ", error);
      return null;
    }
  },
  deleteCampaignRegisteredChannel: async (Id: number): Promise<boolean> => {
    try {
      const token = getToken();
      if (!token) return false;
      await campaignApi.deleteCampaignRegisteredChannel(token, Id);
      return true;
    } catch (error) {
      console.error("Failed to delete Campaign Registered Channel: ", error);
      return false;
    }
  },
  updateCampaignRegisteredChannelEmail: async (
    brandId: string,
    email: string,
  ): Promise<boolean> => {
    try {
      const token = getToken();
      if (!token) return false;
      await campaignApi.updateCampaignRegisteredChannelEmail(token, brandId, email);
      return true;
    } catch (error) {
      console.error("Failed to update Campaign Registered Channel email: ", error);
      return false;
    }
  },
  initClientId: async (email: string): Promise<any> => {
    try {
      const secret = process.env.REACT_APP_WHATSAPP_SECRET;
      if (!secret) return null;
      if (email) {
        const response = await campaignApi.initClientId(secret, email);
        return response;
      }
      return null;
    } catch (error) {
      console.error("Failed to generate QR:", error);
      return null;
    }
  },
  generateQR: async (email: string): Promise<any> => {
    try {
      const secret = process.env.REACT_APP_WHATSAPP_SECRET;
      if (!secret) return null;
      if (email) {
        const response = await campaignApi.generateQR(secret, email);
        return response.data;
      }
      return null;
    } catch (error: any) {
      console.error("Failed to generate QR:", error);
      return error?.response?.data;
    }
  },
  checkActivate: async (email: string): Promise<any> => {
    try {
      const secret = process.env.REACT_APP_WHATSAPP_SECRET;
      if (!secret) return null;
      if (email) {
        const response = await campaignApi.checkActivate(secret, email);
        return response.data;
      }
      return null;
    } catch (error) {
      console.error("Failed to check activate:", error);
      return null;
    }
  },
  checkAvailableCampaignName: async (
    name: string,
    id?: number,
  ): Promise<any> => {
    try {
      const token = getToken();
      if (!token) return null;
      if (name) {
        const response = await campaignApi.checkAvailableCampaignName(
          token,
          name,
          id,
        );
        return response.data.result;
      }
      return null;
    } catch (error) {
      console.error("Failed to check Available Campaign Name:", error);
      return null;
    }
  },
  createCampaignDetails: async (
    campaignDetails: CampaignDetails,
    exclusionFile?: File,
  ): Promise<any> => {
    try {
      const token = getToken();
      if (!token) return null;
      if (campaignDetails) {
        const response = await campaignApi.createCampaignDetails(
          token,
          campaignDetails,
          exclusionFile,
        );
        return response.data.result.Id;
      }
      return null;
    } catch (error) {
      console.error("Failed to create campaign:", error);
      return null;
    }
  },
  updateCampaignDetails: async (
    campaignDetails: CampaignDetails,
    exclusionFile?: File,
  ): Promise<any> => {
    try {
      const token = getToken();
      if (!token) return null;
      if (campaignDetails) {
        const response = await campaignApi.updateCampaignDetails(
          token,
          campaignDetails,
          exclusionFile,
        );
        return response.data;
      }
      return null;
    } catch (error) {
      console.error("Failed to create campaign:", error);
      return null;
    }
  },
  updateCampaignDetailsStatus: async (
    id: number,
    status: StatusEnum,
    isBilled: boolean = false,
    referenceCode?: string,
  ): Promise<any> => {
    try {
      const token = getToken();
      if (!token) return null;
      if (id && status) {
        const response = await campaignApi.updateCampaignDetailsStatus(
          token,
          id,
          status,
          isBilled,
          referenceCode,
        );
        return response.data.status;
      }
      return null;
    } catch (error) {
      console.error("Failed to create campaign:", error);
      return null;
    }
  },
  getCampaignDetailsById: async (id: number): Promise<any> => {
    try {
      const token = getToken();
      if (!token) return null;
      if (id) {
        const response = await campaignApi.getCampaignDetailsById(token, id);
        return response.data.result.CampaignDetails;
      }
      return null;
    } catch (error) {
      console.error("Failed to get campaign by id:", error);
      return null;
    }
  },
  deleteCampaignDetails: async (id: number): Promise<any> => {
    try {
      const token = getToken();
      if (!token) return null;
      if (id) {
        const response = await campaignApi.deleteCampaignDetails(token, id);
        return response.data.status;
      }
      return null;
    } catch (error) {
      console.error("Failed to get campaign by id:", error);
      return null;
    }
  },
  estimatedTotalMember: async (
    estimatedTotalMembersRequest: EstimatedTotalMembersRequest,
    exclusionFile?: File,
  ): Promise<any> => {
    try {
      const token = getToken();
      if (!token) return null;
      if (estimatedTotalMembersRequest) {
        const response = await campaignApi.estimatedTotalMember(
          token,
          estimatedTotalMembersRequest,
          exclusionFile,
        );
        return response.data.result;
      }
      return null;
    } catch (error) {
      console.error("Failed to get estimated total member :", error);
      return null;
    }
  },
  getAllPostalDistrict: async (): Promise<any> => {
    try {
      const token = getToken();
      if (!token) return null;
      const response = await campaignApi.getAllPostalDistrict(token);
      return response.data.result.PostalCode;
    } catch (error) {
      console.error("Failed to fetch all postal code :", error);
      return null;
    }
  },
  uploadCampaignImage: async (file: File): Promise<any> => {
    try {
      const token = getToken();
      if (!token) return null;
      const response = await campaignApi.uploadCampaignImage(token, file);
      return response.data;
    } catch (error) {
      console.error("Failed to upload campaign image :", error);
      return null;
    }
  },
  getBlockedCampaignDateRanges: async (
    brandId: string,
    channel: ChannelEnum | null,
  ): Promise<DateRange[] | null> => {
    try {
      const token = getToken();
      if (!token) return null;
      const response = await campaignApi.getBlockedCampaignDateRanges(
        token,
        brandId,
        channel,
      );
      return response.data.result.BlockedDateRanges;
    } catch (error) {
      console.error("Failed to get blocked campaign date ranges:", error);
      return null;
    }
  },
  getListAccountBrand: async (
    brandId: string,
  ): Promise<AccountBrandOutletSetting[] | null> => {
    try {
      const token = getToken();
      if (!token) return null;
      if (brandId) {
        const response = await campaignApi.getListAccountBrand(token, brandId);
        return response.data.result;
      }
      return null;
    } catch (error) {
      console.error("Failed to get blocked campaign date ranges:", error);
      return null;
    }
  },
  getTopPerformingCampaigns: async (
    brandId: string,
    count: number | null,
  ): Promise<CompletedCampaignViewModel[] | null> => {
    try {
      const token = getToken();
      if (!token) return null;
      if (brandId) {
        const response = await campaignApi.getTopPerformingCampaigns(
          token,
          brandId,
          count,
        );
        return response.data.result;
      }
      return null;
    } catch (error) {
      console.error("Failed to get blocked campaign date ranges:", error);
      return null;
    }
  },
  getAllVouchers: async (): Promise<Voucher[] | null> => {
    try {
      const token = getToken();
      if (!token) return null;
      const response = await campaignApi.getAllVouchers(token);
      return response.data.result;
    } catch (error) {
      console.error("Failed to get voucher:", error);
      return null;
    }
  },
  getCampaignInfo: async (brandId: string): Promise<CampaignInfo | null> => {
    try {
      const token = getToken();
      if (!token) return null;
      if (brandId) {
        const response = await campaignApi.getCampaignInfo(token, brandId);
        return response.data.result;
      }
      return null;
    } catch (error) {
      console.error("Failed to get campaign info:", error);
      return null;
    }
  },
  getAllEVouchers: async (email: string): Promise<EVoucher[] | null> => {
    try {
      if (!email) return null;
      const response = await campaignApi.getAllEVouchers(email);
      return response.data.data.eVouchers;
    } catch (error) {
      console.error("Failed to get voucher:", error);
      return null;
    }
  },
  createCampaignLog: async (id: number): Promise<any> => {
    try {
      const token = getToken();
      if (!token) return null;
      if (id) {
        const response = await campaignApi.createCampaignLog(token, id);
        return response.data.result;
      }
      return null;
    } catch (error) {
      console.error("Failed to create campaign log:", error);
      return null;
    }
  },
  getAllMessageTemplates: async (): Promise<MessageTemplateData[] | null> => {
    try {
      const token = getToken();
      if (!token) return null;
      const response = await campaignApi.getAllMessageTemplates(token);
      return response.data.result;
    } catch (error) {
      console.error("Failed to get all message templates:", error);
      return null;
    }
  },
  checkAccountCreditCard: async (email: string): Promise<boolean | null> => {
    try {
      const token = getToken();
      if (!token || !email) return null;
      const response = await campaignApi.checkAccountCreditCard(token, email);
      return response.data.result ?? false;
    } catch (error) {
      console.error("Failed to check account credit card:", error);
      return null;
    }
  },
  processPayment: async (
    campaignId: number,
    email: string,
  ): Promise<boolean | null> => {
    try {
      const token = getToken();
      if (!token) return null;
      const response = await campaignApi.processPayment(
        token,
        campaignId,
        email,
      );
      return response.data.result ?? false;
    } catch (error) {
      console.error("Failed to process payment:", error);
      return null;
    }
  },
  updateCampaignBilled: async (campaignId: number, referenceCode?: string): Promise<boolean | null> => {
    try {
      const token = getToken();
      if (!token) return null;
      const response = await campaignApi.updateCampaignBilled(
        token,
        campaignId,
        referenceCode,
      );
      return response.data.result ?? false;
    } catch (error) {
      console.error("Failed to process payment:", error);
      return null;
    }
  },
  downloadRecipient: async (id: number, campaignRunLogId?: number): Promise<any> => {
    try {
      const token = getToken();
      if (!token) return null;

      const response = await campaignApi.downloadRecipient(token, id, campaignRunLogId);
      return response.data;
    } catch (error) {
      console.error("Failed to download recipient:", error);
      return null;
    }
  },
  rescheduleCampaign: async (
    id: number,
    campaignStart: string,
    campaignEnd: string,
  ): Promise<boolean | null> => {
    try {
      const token = getToken();
      if (!token) return null;
      const response = await campaignApi.rescheduleCampaign(
        token,
        id,
        campaignStart,
        campaignEnd,
      );
      return response.data.result ?? false;
    } catch (error) {
      console.error("Failed to reschedule campaign:", error);
      return null;
    }
  },
  getEmailPreview: async (
    type: number | undefined,
    brandId: string,
    voucherId?: number,
  ): Promise<string | null> => {
    try {
      const token = getToken();
      if (!token) return null;
      const response = await campaignApi.getEmailPreview(
        token,
        type,
        brandId,
        voucherId,
      );
      return response.data as string;
    } catch (error) {
      console.error("Failed to fetch email preview:", error);
      return null;
    }
  },
};
